import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, ArrowRight, ArrowLeft, Clock, Calendar, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Questions', 'Date & Time', 'Your Details', 'Confirmation'];

const LOGO_PNG = "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/q2693oj8_rsz_untitled_design_5-1-Photoroom.png";

const BookingPage = () => {
  const [step, setStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [availability, setAvailability] = useState([]);
  const [settings, setSettings] = useState({ admin_timezone: 'Asia/Dhaka', time_format: '12' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [userDetails, setUserDetails] = useState({ name: '', email: '', phone: '' });
  const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [loading, setLoading] = useState(false);
  const [booked, setBooked] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, aRes, sRes] = await Promise.all([
          axios.get(`${API}/booking/questions`),
          axios.get(`${API}/booking/availability`),
          axios.get(`${API}/booking/settings`)
        ]);
        setQuestions(qRes.data);
        setAvailability(aRes.data);
        setSettings(sRes.data);
        if (qRes.data.length === 0) setStep(1);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const availableDatesSet = useMemo(() => {
    return new Set(availability.map(d => d.date));
  }, [availability]);

  const selectedDateSlots = useMemo(() => {
    if (!selectedDate) return [];
    const dateObj = availability.find(d => d.date === selectedDate);
    return dateObj ? dateObj.slots.filter(s => s.status === 'available') : [];
  }, [selectedDate, availability]);

  const formatTime = (time) => {
    if (settings.time_format === '24') return time;
    const [h, m] = time.split(':');
    const hr = parseInt(h);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    const hr12 = hr % 12 || 12;
    return `${hr12}:${m} ${ampm}`;
  };

  const canProceed = () => {
    if (step === 0) {
      return questions.filter(q => q.required).every(q => answers[q.id]?.trim());
    }
    if (step === 1) return selectedDate && selectedSlot;
    if (step === 2) return userDetails.name.trim() && userDetails.email.trim();
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const dateObj = availability.find(d => d.date === selectedDate);
      const res = await axios.post(`${API}/booking/create`, {
        date_id: dateObj.id,
        slot_id: selectedSlot.id,
        user_name: userDetails.name,
        user_email: userDetails.email,
        user_phone: userDetails.phone || null,
        user_timezone: userTimezone,
        questionnaire_answers: answers
      });
      setBookingResult(res.data.booking);
      setBooked(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderCalendar = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date().toISOString().split('T')[0];
    const days = [];

    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isAvailable = availableDatesSet.has(dateStr) && dateStr >= today;
      days.push({ day: d, date: dateStr, isAvailable });
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCalendarMonth(new Date(year, month - 1))} className="p-2 hover:bg-slate-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h3 className="font-semibold text-slate-800">
            {calendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={() => setCalendarMonth(new Date(year, month + 1))} className="p-2 hover:bg-slate-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-500 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {days.map((d, i) => d ? (
            <button
              key={i}
              disabled={!d.isAvailable}
              onClick={() => { setSelectedDate(d.date); setSelectedSlot(null); }}
              data-testid={`cal-day-${d.day}`}
              className={`relative p-2.5 rounded-xl text-sm font-medium transition-all ${
                selectedDate === d.date
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30 scale-105'
                  : d.isAvailable
                    ? 'bg-gradient-to-br from-blue-50 to-sky-50 text-blue-700 hover:from-blue-100 hover:to-sky-100 cursor-pointer border border-blue-200 hover:shadow-md'
                    : 'text-slate-300 cursor-not-allowed'
              }`}
            >
              {d.day}
              {d.isAvailable && selectedDate !== d.date && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
              )}
            </button>
          ) : <div key={i} />)}
        </div>
      </div>
    );
  };

  if (booked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h2>
          <p className="text-slate-500 mb-6">A confirmation email has been sent to your inbox.</p>
          <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2 mb-6">
            <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-medium">{bookingResult?.date}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Time</span><span className="font-medium">{formatTime(bookingResult?.start_time || '')} - {formatTime(bookingResult?.end_time || '')}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Timezone</span><span className="font-medium">{userTimezone}</span></div>
          </div>
          <Button onClick={() => window.location.href = '/'} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-5">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header with Logo */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between sm:justify-start">
          <a href="/">
            <img src={LOGO_PNG} alt="VirtualSwift" className="h-9 object-contain" />
          </a>
          <h2 className="text-sm font-semibold text-slate-600 sm:hidden">Book a Meeting</h2>
          <div className="hidden sm:block sm:ml-4 sm:pl-4 sm:border-l sm:border-slate-200">
            <span className="text-sm font-medium text-slate-500">Book a Meeting</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-16">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Book a Meeting</h1>
          <p className="text-slate-500">Schedule a call with our team</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => {
            if (i === 0 && questions.length === 0) return null;
            return (
              <React.Fragment key={i}>
                {i > 0 && (i !== 1 || questions.length > 0) && <div className={`h-0.5 w-8 sm:w-12 ${i <= step ? 'bg-blue-500' : 'bg-slate-200'}`} />}
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium ${
                  i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  <span className="hidden sm:inline">{s}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 sm:p-10">
          {/* Step 0: Questions */}
          {step === 0 && questions.length > 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">A few questions first</h2>
              {questions.map((q) => (
                <div key={q.id} className="space-y-2">
                  <Label className="font-medium text-slate-700">
                    {q.question} {q.required && <span className="text-red-500">*</span>}
                  </Label>
                  {q.question_type === 'text' && (
                    <Input
                      data-testid={`q-${q.id}`}
                      value={answers[q.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                      className="rounded-xl border-slate-200"
                    />
                  )}
                  {q.question_type === 'textarea' && (
                    <Textarea
                      data-testid={`q-${q.id}`}
                      value={answers[q.id] || ''}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                      rows={3}
                      className="rounded-xl border-slate-200"
                    />
                  )}
                  {(q.question_type === 'select') && (
                    <Select value={answers[q.id] || ''} onValueChange={(v) => setAnswers({ ...answers, [q.id]: v })}>
                      <SelectTrigger data-testid={`q-${q.id}`} className="rounded-xl border-slate-200"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {q.options.map((opt, idx) => <SelectItem key={idx} value={opt}>{opt}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {q.question_type === 'radio' && (
                    <div className="space-y-2">
                      {q.options.map((opt, idx) => {
                        const selected = (answers[q.id] || '').split('||').filter(Boolean);
                        const isChecked = selected.includes(opt);
                        return (
                          <label key={idx} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                            isChecked ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                          }`}>
                            <input
                              type="checkbox" value={opt}
                              checked={isChecked}
                              onChange={() => {
                                const newSelected = isChecked
                                  ? selected.filter(s => s !== opt)
                                  : [...selected, opt];
                                setAnswers({ ...answers, [q.id]: newSelected.join('||') });
                              }}
                              className="accent-blue-600 w-4 h-4"
                            />
                            <span className="text-sm text-slate-700">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Date & Time */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" /> Select Date & Time
              </h2>
              <p className="text-sm text-slate-500">Timezone: {userTimezone}</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-2xl p-4">{renderCalendar()}</div>
                <div>
                  {selectedDate ? (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-slate-700 text-sm">Available slots for {selectedDate}</h3>
                      {selectedDateSlots.length === 0 ? (
                        <p className="text-slate-400 text-sm">No available slots</p>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          {selectedDateSlots.map((slot) => (
                            <button
                              key={slot.id}
                              data-testid={`slot-${slot.id}`}
                              onClick={() => setSelectedSlot(slot)}
                              className={`p-3 rounded-xl text-sm font-medium border transition-all flex items-center gap-2 ${
                                selectedSlot?.id === slot.id
                                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                                  : 'border-slate-200 hover:border-blue-300 text-slate-600'
                              }`}
                            >
                              <Clock className="w-4 h-4" />
                              {formatTime(slot.start_time)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                      Select a highlighted date
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" /> Your Details
              </h2>
              <div className="space-y-4 max-w-md">
                <div>
                  <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Full Name *</Label>
                  <Input
                    data-testid="booking-name"
                    required value={userDetails.name}
                    onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12 rounded-xl border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Email *</Label>
                  <Input
                    data-testid="booking-email"
                    type="email" required value={userDetails.email}
                    onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                    placeholder="you@email.com"
                    className="h-12 rounded-xl border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-600 mb-1.5 block">Phone (optional)</Label>
                  <Input
                    data-testid="booking-phone"
                    value={userDetails.phone}
                    onChange={(e) => setUserDetails({ ...userDetails, phone: e.target.value })}
                    placeholder="+880..."
                    className="h-12 rounded-xl border-slate-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-900">Confirm Your Booking</h2>
              <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><p className="text-xs text-slate-500 uppercase font-semibold">Date</p><p className="font-bold text-slate-800">{selectedDate}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase font-semibold">Time</p><p className="font-bold text-slate-800">{selectedSlot && `${formatTime(selectedSlot.start_time)} - ${formatTime(selectedSlot.end_time)}`}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase font-semibold">Name</p><p className="font-bold text-slate-800">{userDetails.name}</p></div>
                  <div><p className="text-xs text-slate-500 uppercase font-semibold">Email</p><p className="font-bold text-slate-800">{userDetails.email}</p></div>
                  {userDetails.phone && <div><p className="text-xs text-slate-500 uppercase font-semibold">Phone</p><p className="font-bold text-slate-800">{userDetails.phone}</p></div>}
                  <div><p className="text-xs text-slate-500 uppercase font-semibold">Timezone</p><p className="font-bold text-slate-800">{userTimezone}</p></div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            {step > (questions.length === 0 ? 1 : 0) ? (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-xl px-6">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            ) : <div />}
            {step < 3 ? (
              <Button
                data-testid="booking-next-btn"
                disabled={!canProceed()}
                onClick={() => setStep(step + 1)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-5"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="booking-confirm-btn"
                disabled={loading}
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-8 py-5"
              >
                {loading ? 'Booking...' : 'Confirm Booking'}
                <CheckCircle className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
